(() => {
  if (window.EcomhardCtaKitQuickView) return;

  const { MinimogThemeStyles = {}, MinimogThemeScripts = {} } = window;

  class EcomhardCtaKitQuickView {
    constructor() {
      this.modal = new MinimogTheme.Modal();
      this.modal.modal && this.modal.modal.classList.add("ecomhard-cta-kit-modal");
      this.isOpen = false;
      this.target = null;
      this.loadedExternalScripts = window.__ecomhardCtaKitLoadedScripts || new Set();
      window.__ecomhardCtaKitLoadedScripts = this.loadedExternalScripts;

      addEventDelegate({
        selector: "[data-ecomhard-cta-kit-open]",
        handler: (event, target) => {
          event.preventDefault();
          const productHandle = target.dataset.productHandle;
          if (!productHandle) return;

          this.target = target;
          this.toggleLoading(true);
          this.load(productHandle);
        },
      });

      window.MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, () => {
        if (!this.isOpen) return;

        this.close();

        // O modal e o cart drawer usam a mesma classe de bloqueio de scroll.
        // Depois da animação de fechamento do modal, preserva o bloqueio caso
        // o drawer já tenha sido aberto pelo product-form do tema.
        setTimeout(() => {
          const cartDrawer = document.querySelector("m-cart-drawer");
          if (cartDrawer && cartDrawer.classList.contains("m-cart-drawer--active")) {
            document.documentElement.classList.add("prevent-scroll");
          }
        }, 260);
      });
    }

    async load(productHandle) {
      try {
        await this.loadAssets(
          [MinimogThemeStyles.product, MinimogThemeStyles.productInventory].filter(Boolean),
          "ecomhard-cta-kit-styles"
        );

        const productUrl = `${window.MinimogSettings.base_url}products/${productHandle}`;

        const [quickViewDocument, productDocument] = await Promise.all([
          fetchSection("product-quickview", { url: productUrl }),
          fetch(productUrl, { credentials: "same-origin" })
            .then((response) => {
              if (!response.ok) throw new Error(`Não foi possível carregar a PDP (${response.status}).`);
              return response.text();
            })
            .then((html) => new DOMParser().parseFromString(html, "text/html")),
        ]);

        const modalContent = quickViewDocument.querySelector(".m-product-quickview");
        const targetMainProduct =
          productDocument.querySelector('.m-main-product[data-section-type="product-page"]') ||
          productDocument.querySelector(".m-main-product");
        const targetInfo = targetMainProduct && targetMainProduct.querySelector(".m-main-product--info .m-product-info--wrapper");

        if (!modalContent || !targetMainProduct || !targetInfo) {
          throw new Error("Não foi possível localizar as informações da PDP do produto selecionado.");
        }

        const modalInfo = modalContent.querySelector(".m-product-quickview--info");
        const modalInfoWrapper = modalInfo && modalInfo.querySelector(".m-product-info--wrapper");
        if (!modalInfo || !modalInfoWrapper) {
          throw new Error("Estrutura do quick view não encontrada.");
        }

        const sourceSectionId = targetMainProduct.dataset.sectionId || "";
        const quickViewSectionId = modalContent.dataset.sectionId || "product-quickview";

        let infoHtml = targetInfo.innerHTML;
        if (sourceSectionId && quickViewSectionId && sourceSectionId !== quickViewSectionId) {
          infoHtml = infoHtml.split(sourceSectionId).join(quickViewSectionId);
        }

        modalInfoWrapper.innerHTML = infoHtml;
        modalInfo.classList.add("m-main-product--info", "m:text-color-body");
        modalContent.classList.add("ecomhard-cta-kit-quickview");
        modalContent.dataset.ecomhardCtaKitQuickview = "true";

        this.prepareInjectedPdp(modalContent, quickViewSectionId);

        await this.loadAssets(
          [
            MinimogThemeScripts.productMedia,
            MinimogThemeScripts.variantsPicker,
            MinimogThemeScripts.productInventory,
          ].filter(Boolean),
          "ecomhard-cta-kit-product-assets"
        );

        this.modal.appendChild(modalContent);
        this.modal.setWidth("1200px");
        this.modal.open();
        this.modal.setSizes(`m-gradient ${modalContent.dataset.colorScheme || ""}`);
        this.isOpen = true;

        this.activateScripts(modalInfoWrapper);

        requestAnimationFrame(() => {
          window.dispatchEvent(new Event("resize"));
        });

        document.dispatchEvent(
          new CustomEvent("quick-view:loaded", {
            detail: {
              productUrl: productHandle,
              source: "ecomhard-cta-kit",
            },
          })
        );

        document.dispatchEvent(
          new CustomEvent("ecomhard:cta-kit:loaded", {
            detail: { productHandle },
          })
        );
      } catch (error) {
        console.error("[ECOMHARD] CTA Kit:", error);
        if (window.MinimogTheme && MinimogTheme.Notification) {
          MinimogTheme.Notification.show({
            target: document.body,
            type: "error",
            message: "Não foi possível abrir o kit. Tente novamente.",
            last: 3000,
          });
        }
      } finally {
        this.toggleLoading(false);
      }
    }

    prepareInjectedPdp(modalContent, quickViewSectionId) {
      modalContent.querySelectorAll(".main-product__block-ecomhard_cta_kit").forEach((block) => block.remove());

      modalContent.querySelectorAll("variant-picker").forEach((picker) => {
        picker.dataset.sectionId = quickViewSectionId;
        picker.dataset.updateUrl = "false";
        picker.dataset.enableVariantGroupImages = "false";
        picker.dataset.showFeaturedMedia = "true";
      });

      this.ensureUniqueIds(modalContent, quickViewSectionId);
    }

    ensureUniqueIds(container, quickViewSectionId) {
      const idMap = new Map();
      const installmentForm = container.querySelector('#product-form-installment');

      // O variant-picker do tema procura #productData, #productVariants e #variantGroup
      // dentro do próprio quick view. Esses IDs precisam permanecer intactos.
      // O formulário de parcelamento, por outro lado, é global na PDP e pode colidir
      // com o produto que já está aberto ao fundo.
      if (installmentForm) {
        const oldId = installmentForm.id;
        const newId = `product-form-installment-${quickViewSectionId}`;
        idMap.set(oldId, newId);
        installmentForm.id = newId;
      }

      if (!idMap.size) return;

      const referenceAttributes = ["for", "form", "aria-controls", "aria-labelledby", "aria-describedby"];
      container.querySelectorAll("*").forEach((element) => {
        referenceAttributes.forEach((attribute) => {
          const value = element.getAttribute(attribute);
          if (!value) return;

          const replaced = value
            .split(/\s+/)
            .map((token) => idMap.get(token) || token)
            .join(" ");

          if (replaced !== value) element.setAttribute(attribute, replaced);
        });

        const href = element.getAttribute("href");
        if (href && href.startsWith("#")) {
          const id = href.slice(1);
          if (idMap.has(id)) element.setAttribute("href", `#${idMap.get(id)}`);
        }
      });
    }

    activateScripts(container) {
      const scripts = [...container.querySelectorAll("script")];

      scripts.forEach((oldScript) => {
        const type = (oldScript.getAttribute("type") || "").toLowerCase();
        const isExecutable = !type || ["text/javascript", "application/javascript", "module"].includes(type);
        if (!isExecutable) return;

        const src = oldScript.getAttribute("src");
        if (src) {
          const absoluteSrc = new URL(src, window.location.href).href;
          const alreadyOnPage = [...document.scripts].some((script) => script.src === absoluteSrc && script !== oldScript);

          if (alreadyOnPage || this.loadedExternalScripts.has(absoluteSrc)) {
            oldScript.remove();
            return;
          }

          this.loadedExternalScripts.add(absoluteSrc);
        }

        const newScript = document.createElement("script");
        [...oldScript.attributes].forEach((attribute) => {
          newScript.setAttribute(attribute.name, attribute.value);
        });
        newScript.textContent = oldScript.textContent;
        oldScript.replaceWith(newScript);
      });
    }

    loadAssets(assets, key) {
      if (!assets.length) return Promise.resolve();
      return new Promise((resolve, reject) => {
        loadAssetsNew(assets, key, resolve, reject);
      });
    }

    close(event) {
      if (!this.modal) return;
      this.modal.close(event);
      this.isOpen = false;
    }

    toggleLoading(isLoading) {
      if (!this.target) return;
      this.target.classList.toggle("m-spinner-loading", isLoading);
      this.target.disabled = isLoading;
    }
  }

  window.EcomhardCtaKitQuickView = new EcomhardCtaKitQuickView();
})();
